import { useEffect, useMemo, useState } from 'react';

const TimelineGauge = ({ region, data, ipType = 'ipv4', onDateChange }) => {
    const [selectedTimestamp, setSelectedTimestamp] = useState(null);

    // Destructure props for cleaner usage
    const { regionData, currentIpType } = {
        regionData: data?.[region],
        currentIpType: ipType
    };

    // Extract all unique dates from all countries in the region
    const timestamps = useMemo(() => {
        if (!data || !regionData) return [];

        const allTimestamps = new Set();

        Object.values(regionData).forEach(countryData => {
            const ipData = countryData[currentIpType];
            if (ipData) {
                Object.keys(ipData).forEach(timestamp => {
                    allTimestamps.add(parseInt(timestamp));
                });
            }
        });

        return Array.from(allTimestamps).sort((a, b) => a - b);
    }, [data, regionData, currentIpType]);

    // Calculate the date range for the continuous slider
    const dateRange = useMemo(() => {
        if (timestamps.length === 0) return { min: 0, max: 0 };
        const [min] = timestamps;
        const max = timestamps[timestamps.length - 1];
        return { min, max };
    }, [timestamps]);

    // Set the last date as default
    useEffect(() => {
        if (timestamps.length > 0 && selectedTimestamp === null) {
            setSelectedTimestamp(timestamps[timestamps.length - 1]);
        }
    }, [timestamps, selectedTimestamp]);

    // Notificar cambios de fecha al componente padre
    useEffect(() => {
        if (selectedTimestamp && onDateChange) {
            const selectedDate = new Date(selectedTimestamp * 1000);
            onDateChange(selectedDate);
        }
    }, [selectedTimestamp, onDateChange]);

    // Function to handle slider change
    const handleSliderChange = (e) => {
        const sliderValue = parseInt(e.target.value);
        setSelectedTimestamp(sliderValue);
    };

    // Calculate progress percentage based on selected timestamp
    const progressPercentage = useMemo(() => {
        if (!selectedTimestamp || dateRange.min === dateRange.max) return 0;
        return ((selectedTimestamp - dateRange.min) / (dateRange.max - dateRange.min)) * 100;
    }, [selectedTimestamp, dateRange]);

    // Format date for vertical display with year
    const formatDate = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return {
            day: date.getDate().toString().padStart(2, '0'),
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            year: date.getFullYear()
        };
    };

    // Format date for title
    const formatDateTitle = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Obtener los primeros de enero dentro del rango de fechas
    const getJanuaryFirsts = useMemo(() => {
        if (timestamps.length === 0) return [];

        const startDate = new Date(timestamps[0] * 1000);
        const endDate = new Date(timestamps[timestamps.length - 1] * 1000);
        const januaryFirsts = [];

        // Start from the year after the first date's year
        let currentYear = startDate.getFullYear();
        if (startDate.getMonth() > 0 || startDate.getDate() > 1) {
            currentYear++;
        }

        while (currentYear <= endDate.getFullYear()) {
            const jan1 = new Date(currentYear, 0, 1);
            const jan1Timestamp = Math.floor(jan1.getTime() / 1000);

            // Only add if it's within the range
            if (jan1Timestamp >= timestamps[0] && jan1Timestamp <= timestamps[timestamps.length - 1]) {
                januaryFirsts.push({
                    timestamp: jan1Timestamp,
                    year: currentYear,
                    position: ((jan1Timestamp - timestamps[0]) / (timestamps[timestamps.length - 1] - timestamps[0])) * 100
                });
            }
            currentYear++;
        }

        return januaryFirsts;
    }, [timestamps]);

    if (timestamps.length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No timeline data available for this region.
            </div>
        );
    }

    return (
        <div style={{ padding: 0 }}>
            {/* Timeline Gauge */}
            <div>
                <h4 style={{
                    margin: '0 0 15px 0',
                    color: '#2d3748',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                }}>
                    📅 Timeline: {formatDateTitle(selectedTimestamp)} ({ipType === 'ipv4' ? 'IPv4' : 'IPv6'})
                </h4>

                <div style={{ position: 'relative' }}>
                    {/* Date labels and January markers - Above the gauge */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '17px',
                        fontSize: '0.65rem',
                        color: '#999',
                        position: 'relative',
                        height: '55px'
                    }}>
                        {/* Start date */}
                        <div>
                            {/* Start date label - 3 rows */}
                            <div style={{
                                position: 'absolute',
                                left: '-10px',
                                top: '30px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                fontSize: '0.6rem',
                                color: '#999',
                                lineHeight: '1.1'
                            }}>
                                <div>{formatDate(timestamps[0]).day}</div>
                                <div>{formatDate(timestamps[0]).month}</div>
                                <div>{formatDate(timestamps[0]).year}</div>
                            </div>
                            {/* Start date indicator line */}
                            <div
                                style={{
                                    position: 'absolute',
                                    left: '0%',
                                    top: '65px',
                                    width: '1px',
                                    height: '10px',
                                    backgroundColor: '#ccc'
                                }}
                            />
                        </div>

                        {/* January 1st markers */}
                        {getJanuaryFirsts.map((jan1, index) => (
                            <div key={jan1.year}>
                                {/* Year label */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${jan1.position}%`,
                                        top: '50px',
                                        transform: 'translateX(-50%)',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.6rem',
                                        color: '#999',
                                        textAlign: 'center'
                                    }}
                                >
                                    {jan1.year}
                                </div>
                                {/* Small indicator line */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${jan1.position}%`,
                                        top: '65px',
                                        width: '1px',
                                        height: '10px',
                                        backgroundColor: '#ccc',
                                        transform: 'translateX(-50%)'
                                    }}
                                />
                            </div>
                        ))}

                        {/* End date */}
                        <div>
                            {/* End date label - 3 rows */}
                            <div style={{
                                position: 'absolute',
                                right: '-10px',
                                top: '30px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                fontSize: '0.6rem',
                                color: '#999',
                                lineHeight: '1.1'
                            }}>
                                <div>{formatDate(timestamps[timestamps.length - 1]).day}</div>
                                <div>{formatDate(timestamps[timestamps.length - 1]).month}</div>
                                <div>{formatDate(timestamps[timestamps.length - 1]).year}</div>
                            </div>
                            {/* End date indicator line */}
                            <div
                                style={{
                                    position: 'absolute',
                                    right: '0%',
                                    top: '65px',
                                    width: '1px',
                                    height: '10px',
                                    backgroundColor: '#ccc'
                                }}
                            />
                        </div>
                    </div>

                    {/* Timeline bar */}
                    <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        position: 'relative',
                        marginBottom: '5px'
                    }}>
                        {/* Progress fill */}
                        <div style={{
                            width: `${progressPercentage}%`,
                            height: '100%',
                            backgroundColor: '#667eea',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>

                    {/* Timeline slider */}
                    <input
                        type="range"
                        min={dateRange.min}
                        max={dateRange.max}
                        value={selectedTimestamp || dateRange.max}
                        onChange={handleSliderChange}
                        className="timeline-slider"
                        style={{ marginBottom: '5px' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TimelineGauge;
